/**
 * NiV Tasks Page — Monday.com-style task board for the SaaS admin.
 *
 * - Collapsible colored lists (groups) with inline-editable task rows
 * - Status / Priority / Type colored options, editable via ConfigPanel
 * - "Done" statuses hidden by default (Show done toggle)
 * - English, LTR island inside the RTL app
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  ListChecks,
  Plus,
  GearSix,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";
import ContentLoader from "../../../components/shared/ContentLoader";
import {
  useGetNivTasksQuery,
  useGetNivTasksConfigQuery,
  useCreateNivTaskMutation,
  useUpdateNivTaskMutation,
  useDeleteNivTaskMutation,
  useUpdateNivTasksConfigMutation,
  useBulkUpdateNivTasksMutation,
} from "../../../store/services/nivApi";
import { DEFAULT_TASKS_CONFIG } from "../../../services/nivFirebase";
import { selectIsNivAdmin } from "../../../store/slices/authSlice";
import { useToast } from "../../../contexts/ToastContext";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import TaskListGroup from "./tasks/TaskListGroup";
import TaskModal from "./tasks/TaskModal";
import ConfigPanel from "./tasks/ConfigPanel";
import "./NivTasksPage.css";

const LIST_COLOR_PALETTE = [
  "#579bfc",
  "#00c875",
  "#fdab3d",
  "#e2445c",
  "#784bd1",
  "#ff5ac4",
  "#66ccff",
  "#a25ddc",
];

const generateListId = () =>
  `l_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function NivTasksPage() {
  const isAuthorized = useSelector(selectIsNivAdmin);
  const { showError } = useToast();

  // API
  const { data: config, isLoading: configLoading } = useGetNivTasksConfigQuery(
    undefined,
    { skip: !isAuthorized }
  );
  const { data: tasks = [], isLoading: tasksLoading } = useGetNivTasksQuery(
    undefined,
    { skip: !isAuthorized }
  );
  const [createTask] = useCreateNivTaskMutation();
  const [updateTask] = useUpdateNivTaskMutation();
  const [deleteTask] = useDeleteNivTaskMutation();
  const [updateConfig] = useUpdateNivTasksConfigMutation();
  const [bulkUpdateTasks] = useBulkUpdateNivTasksMutation();

  // UI state
  const [showDone, setShowDone] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [listToDelete, setListToDelete] = useState(null);

  // Column sort: { field, dir } | null. null => manual order (drag enabled).
  const [sort, setSort] = useState(null);
  // Drag & drop state
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null); // { listId, index } | null

  const handleSortChange = (field) => {
    setSort((prev) => {
      if (!prev || prev.field !== field) return { field, dir: "asc" };
      if (prev.dir === "asc") return { field, dir: "desc" };
      return null; // third click clears -> back to manual order
    });
  };

  const isDragEnabled = sort === null;

  // Seed default config once if the config doc doesn't exist yet
  const seededRef = useRef(false);
  useEffect(() => {
    if (!configLoading && config === null && !seededRef.current && isAuthorized) {
      seededRef.current = true;
      updateConfig({ data: DEFAULT_TASKS_CONFIG });
    }
  }, [configLoading, config, isAuthorized, updateConfig]);

  // Derived data
  const lists = useMemo(
    () => [...(config?.lists || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [config]
  );

  const doneStatusIds = useMemo(
    () =>
      new Set(
        (config?.options?.status || [])
          .filter((o) => o.isDone === true)
          .map((o) => o.id)
      ),
    [config]
  );

  // Comparator for column sort. null => manual order.
  const sortComparator = useMemo(() => {
    if (!sort) return null;
    const { field, dir } = sort;
    const mult = dir === "desc" ? -1 : 1;
    const optIndex = (val) => {
      const opts = config?.options?.[field] || [];
      const i = opts.findIndex((o) => o.id === val);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return (a, b) => {
      let cmp;
      if (field === "title") {
        cmp = (a.title || "").localeCompare(b.title || "");
      } else {
        cmp = optIndex(a[field]) - optIndex(b[field]);
      }
      if (cmp === 0) cmp = (a.order ?? 0) - (b.order ?? 0);
      return cmp * mult;
    };
  }, [sort, config]);

  const tasksByList = useMemo(() => {
    const listIds = new Set(lists.map((l) => l.id));
    const firstListId = lists[0]?.id;
    const map = {};
    lists.forEach((l) => {
      map[l.id] = { visible: [], hiddenDone: 0 };
    });

    tasks.forEach((task) => {
      const listId = task.listId && listIds.has(task.listId) ? task.listId : firstListId;
      if (!listId || !map[listId]) return;
      if (!showDone && doneStatusIds.has(task.status)) {
        map[listId].hiddenDone += 1;
        return;
      }
      map[listId].visible.push(task);
    });

    Object.values(map).forEach((entry) => {
      entry.visible.sort(
        sortComparator ||
          ((a, b) => {
            const orderA = a.order ?? 0;
            const orderB = b.order ?? 0;
            if (orderA !== orderB) return orderA - orderB;
            return (a.createdAt || "").localeCompare(b.createdAt || "");
          })
      );
    });

    return map;
  }, [tasks, lists, showDone, doneStatusIds, sortComparator]);

  // ---- Handlers: tasks ----

  const handleAddTask = async (listId, title) => {
    const listTasks = tasks.filter((t) => (t.listId || lists[0]?.id) === listId);
    const maxOrder = listTasks.reduce((max, t) => Math.max(max, t.order || 0), 0);
    const firstNonDone = (config?.options?.status || []).find((o) => o.isDone !== true);
    const defaultPriority = config?.options?.priority?.[0];

    const result = await createTask({
      taskData: {
        title,
        listId,
        status: firstNonDone?.id || "todo",
        priority: defaultPriority?.id || "medium",
        type: null,
        order: maxOrder + 1000,
      },
    });
    if (result.error) showError("Failed to create task");
  };

  const handleUpdateTask = async (taskId, data) => {
    const result = await updateTask({ taskId, data });
    if (result.error) showError("Failed to update task");
  };

  const handleDeleteTask = async () => {
    const result = await deleteTask({ taskId: taskToDelete.id });
    if (result.error) showError("Failed to delete task");
    setTaskToDelete(null);
  };

  // ---- Handlers: lists (all are config-doc writes) ----

  const saveLists = async (newLists) => {
    const result = await updateConfig({ data: { lists: newLists } });
    if (result.error) showError("Failed to update lists");
  };

  const handleAddList = () => {
    const maxOrder = lists.reduce((max, l) => Math.max(max, l.order || 0), 0);
    const color = LIST_COLOR_PALETTE[lists.length % LIST_COLOR_PALETTE.length];
    saveLists([
      ...lists,
      { id: generateListId(), name: "New List", color, collapsed: false, order: maxOrder + 1000 },
    ]);
  };

  const handleToggleCollapse = (listId) => {
    saveLists(lists.map((l) => (l.id === listId ? { ...l, collapsed: l.collapsed !== true } : l)));
  };

  const handleRenameList = (listId, name) => {
    saveLists(lists.map((l) => (l.id === listId ? { ...l, name } : l)));
  };

  const handleRecolorList = (listId, color) => {
    saveLists(lists.map((l) => (l.id === listId ? { ...l, color } : l)));
  };

  const handleMoveList = (listId, direction) => {
    const index = lists.findIndex((l) => l.id === listId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= lists.length) return;
    const reordered = [...lists];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    saveLists(reordered.map((l, i) => ({ ...l, order: (i + 1) * 1000 })));
  };

  const handleDeleteListRequest = (list) => {
    if (lists.length <= 1) {
      showError("Cannot delete the last list");
      return;
    }
    setListToDelete(list);
  };

  const handleDeleteList = async () => {
    const list = listToDelete;
    setListToDelete(null);
    const fallback = lists.find((l) => l.id !== list.id);
    const affected = tasks
      .filter((t) => (t.listId && lists.some((l) => l.id === t.listId) ? t.listId : lists[0]?.id) === list.id)
      .map((t) => t.id);

    if (affected.length > 0) {
      const result = await bulkUpdateTasks({ taskIds: affected, data: { listId: fallback.id } });
      if (result.error) {
        showError("Failed to move tasks out of the list");
        return;
      }
    }
    saveLists(lists.filter((l) => l.id !== list.id));
  };

  // ---- Handlers: drag & drop reorder ----

  const handleDragStart = (taskId) => {
    setDraggedTaskId(taskId);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
    setDropIndicator(null);
  };

  // index = insertion position within the target list's visible array
  const handleDragOver = (listId, index) => {
    setDropIndicator((prev) =>
      prev && prev.listId === listId && prev.index === index ? prev : { listId, index }
    );
  };

  const handleDrop = async () => {
    const indicator = dropIndicator;
    const taskId = draggedTaskId;
    setDraggedTaskId(null);
    setDropIndicator(null);
    if (!indicator || !taskId) return;

    const { listId, index } = indicator;
    // Neighbors as currently seen, excluding the dragged task itself
    const visible = (tasksByList[listId]?.visible || []).filter((t) => t.id !== taskId);
    const dragged = tasks.find((t) => t.id === taskId);
    if (!dragged) return;

    // Clamp index against the filtered list
    const insertAt = Math.max(0, Math.min(index, visible.length));
    const prev = visible[insertAt - 1];
    const next = visible[insertAt];

    let newOrder;
    if (!prev && !next) newOrder = 1000;
    else if (!prev) newOrder = (next.order ?? 0) - 1000;
    else if (!next) newOrder = (prev.order ?? 0) + 1000;
    else newOrder = ((prev.order ?? 0) + (next.order ?? 0)) / 2;

    const sameList = (dragged.listId || lists[0]?.id) === listId;
    const noMove =
      sameList &&
      (prev?.id === taskId ||
        next?.id === taskId ||
        (dragged.order === newOrder));
    if (noMove) return;

    const data = { order: newOrder };
    if (!sameList) data.listId = listId;
    const result = await updateTask({ taskId, data });
    if (result.error) showError("Failed to reorder task");
  };

  // ---- Handlers: config options ----

  const handleSaveOptions = (field, newOptions) => {
    updateConfig({
      data: { options: { ...(config?.options || {}), [field]: newOptions } },
    }).then((result) => {
      if (result.error) showError("Failed to update config");
    });
  };

  const handleReassignTasks = async (field, taskIds, toId) => {
    const result = await bulkUpdateTasks({ taskIds, data: { [field]: toId } });
    if (result.error) {
      showError("Failed to reassign tasks");
      throw new Error("reassign failed");
    }
  };

  // ---- Render ----

  if (!isAuthorized) {
    return (
      <div className="nivtasks-page" dir="ltr">
        <div className="nivtasks-unauthorized">Access denied</div>
      </div>
    );
  }

  const isLoading = configLoading || tasksLoading || !config;

  return (
    <div className="nivtasks-page" dir="ltr">
      <header className="nivtasks-header">
        <div className="nivtasks-header-title">
          <ListChecks size={26} weight="duotone" />
          <h1>Tasks</h1>
        </div>
        <div className="nivtasks-toolbar">
          <button
            type="button"
            className={`nivtasks-btn nivtasks-btn-ghost ${showDone ? "is-active" : ""}`}
            onClick={() => setShowDone(!showDone)}
          >
            {showDone ? <EyeSlash size={16} /> : <Eye size={16} />}
            {showDone ? "Hide done" : "Show done"}
          </button>
          <button
            type="button"
            className={`nivtasks-btn nivtasks-btn-ghost ${showConfig ? "is-active" : ""}`}
            onClick={() => setShowConfig(!showConfig)}
          >
            <GearSix size={16} />
            Config
          </button>
          <button type="button" className="nivtasks-btn nivtasks-btn-primary" onClick={handleAddList}>
            <Plus size={16} weight="bold" />
            New list
          </button>
        </div>
      </header>

      {isLoading ? (
        <ContentLoader message="Loading board…" />
      ) : (
        <>
          {showConfig && (
            <ConfigPanel
              config={config}
              tasks={tasks}
              onSaveOptions={handleSaveOptions}
              onReassignTasks={handleReassignTasks}
              showError={showError}
            />
          )}

          <div className="nivtasks-groups">
            {lists.map((list, index) => (
              <TaskListGroup
                key={list.id}
                list={list}
                tasks={tasksByList[list.id]?.visible || []}
                hiddenDoneCount={tasksByList[list.id]?.hiddenDone || 0}
                config={config}
                listActions={{
                  onToggleCollapse: handleToggleCollapse,
                  onRename: handleRenameList,
                  onRecolor: handleRecolorList,
                  onMoveUp: (id) => handleMoveList(id, -1),
                  onMoveDown: (id) => handleMoveList(id, 1),
                  onDelete: handleDeleteListRequest,
                  canMoveUp: index > 0,
                  canMoveDown: index < lists.length - 1,
                }}
                taskActions={{
                  onUpdate: handleUpdateTask,
                  onDelete: setTaskToDelete,
                  onOpenModal: setModalTask,
                  onAdd: handleAddTask,
                }}
                sort={sort}
                onSortChange={handleSortChange}
                dnd={{
                  enabled: isDragEnabled,
                  draggedTaskId,
                  dropIndex:
                    dropIndicator?.listId === list.id ? dropIndicator.index : null,
                  onDragStart: handleDragStart,
                  onDragEnd: handleDragEnd,
                  onDragOver: handleDragOver,
                  onDrop: handleDrop,
                }}
              />
            ))}

            {lists.length === 0 && (
              <div className="nivtasks-empty-board">
                <p>No lists yet.</p>
                <button type="button" className="nivtasks-btn nivtasks-btn-primary" onClick={handleAddList}>
                  <Plus size={16} weight="bold" /> Create your first list
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {modalTask && (
        <TaskModal
          task={tasks.find((t) => t.id === modalTask.id) || modalTask}
          config={config}
          onSave={handleUpdateTask}
          onDelete={setTaskToDelete}
          onClose={() => setModalTask(null)}
        />
      )}

      <ConfirmDialog
        isOpen={taskToDelete !== null}
        onClose={() => setTaskToDelete(null)}
        onConfirm={handleDeleteTask}
        title="Delete task"
        message={taskToDelete ? `Delete "${taskToDelete.title}"? This cannot be undone.` : ""}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        icon="delete"
      />

      <ConfirmDialog
        isOpen={listToDelete !== null}
        onClose={() => setListToDelete(null)}
        onConfirm={handleDeleteList}
        title="Delete list"
        message={
          listToDelete
            ? `Delete "${listToDelete.name}"? Its tasks will be moved to another list.`
            : ""
        }
        confirmText="Delete list"
        cancelText="Cancel"
        type="danger"
        icon="delete"
      />
    </div>
  );
}

export default NivTasksPage;
